import React, { useContext } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import { useRouter } from "../../hooks/useNavigation";
import { transparentize } from "polished";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import getQueryString from "../../helpers/getQueryString/getQueryString";
import Markdown from "../../styles/Markdown";
import {
  horizontalAlignmentMap,
  verticalAlignmentMap,
} from "../../styles/positionMaps";

const StyledRoleName = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const StyledOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${({ theme, $showOverlay }) =>
    $showOverlay && `${transparentize(0.6, theme.primaryColor)}`};
  z-index: 1;
`;

const StyledRoleThumbnail = styled.a`
  background-color : ${({ theme }) => theme.primaryBackgroundColor}
  height: ${({ dimensions }) => dimensions?.height};
  width: ${({ dimensions }) => dimensions?.width || "100%"};
  min-height: ${({ dimensions }) => dimensions?.height || "175px"};
  max-height: 400px;
  max-width: 100%;
`;

function RoleCards() {
  const { rolesStore, filtersStore } = useStores();
  const { roles } = rolesStore;
  const { setFilter, applyFilters, resetFilters } = filtersStore;

  const router = useRouter();

  const {
    cardHeight,
    cardWidth,
    resultsPageHref,
    horizontalTextAlignment,
    verticalTextAlignment,
    roleCardClassName,
    showOverlay,
    thumbnailClassName,
    text,
    rolesInRow = 3,
    objectFit,
  } = useContext(ComponentContext);

  const handleClick = (role, event) => {
    event.preventDefault();
    resetFilters();
    setFilter("category", role.roleName);
    applyFilters();

    const query = getQueryString({ category: role.roleName });

    router.push({
      pathname: resultsPageHref || `/roles/${role.slug}/results`,
      query,
    });
  };

  const roleNameClassName = classNames(
    "d-flex p-4 position-absolute",
    `justify-content-${
      horizontalAlignmentMap[horizontalTextAlignment || "center"]
    }`,
    `align-items-${verticalAlignmentMap[verticalTextAlignment || "center"]}`
  );

  const singleRoleCardClassName = classNames(
    "col-md-6 mb-4 d-flex justify-content-center",
    `col-lg-${Math.round(12 / rolesInRow)}`,
    {
      [roleCardClassName]: !isEmpty(roleCardClassName),
    }
  );

  const roleThumbnailClassName = classNames("position-relative", {
    [thumbnailClassName]: !isEmpty(thumbnailClassName),
  });

  return (
    <>
      {text && (
        <div>
          <Markdown>{text}</Markdown>
        </div>
      )}

      <div className="row py-3">
        {roles.map((role) => (
          <div
            key={`${role.roleName}-${role.id}`}
            className={singleRoleCardClassName}
          >
            <StyledRoleThumbnail
              className={roleThumbnailClassName}
              dimensions={{ height: cardHeight, width: cardWidth }}
              href={resultsPageHref || `/roles/${role.slug}/results`}
              onClick={(event) => handleClick(role, event)}
            >
              {role?.thumbnail?.url && (
                <Image
                  alt={role.roleName}
                  layout="fill"
                  objectFit={objectFit}
                  priority
                  src={role?.thumbnail?.url}
                />
              )}
              <StyledRoleName className={roleNameClassName}>
                <h3 className="m-0 text-white text-uppercase">
                  {role.roleName}
                </h3>
              </StyledRoleName>
              {showOverlay && <StyledOverlay $showOverlay={showOverlay} />}
            </StyledRoleThumbnail>
          </div>
        ))}
      </div>
    </>
  );
}

export default observer(RoleCards);
